import React, { Fragment } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute } from '@react-navigation/native';

import {
  AuthFormLayout,
  Form,
  Field,
  FormButton,
  AuthLayout,
  Separator,
  Button,
  ButtonStyleEnum,
} from '@components';
import {
  setNewPasswordSchema,
  SetNewPasswordSchemaDataType,
} from '@validation';
import { useAuthModel } from '@models/AuthModel';
import { ToastService } from '@services';
import { IS_MOBILE, parseApiError } from '@utils';
import {
  AuthNavigatorRoutePropType,
  AuthNavigatorNavigationPropType,
  screens,
} from '@navigation';
import { useNavOptionsSet } from '@hooks';

type RoutePropType = AuthNavigatorRoutePropType<'SetNewPassword'>;
type NavigationPropType =
  AuthNavigatorNavigationPropType<'SetNewPassword'>;

export const SetNewPasswordScreen = observer(() => {
  const { t } = useTranslation();
  const authModel = useAuthModel();
  const route = useRoute<RoutePropType>();
  const nav = useNavigation<NavigationPropType>();

  useNavOptionsSet({ title: t('setNewPassword.title') });

  async function setNewPassword(
    values: SetNewPasswordSchemaDataType,
  ) {
    try {
      await authModel.setNewPassword.run({
        password: values.password,
        restore: route.params.restore,
      });

      ToastService.showSuccess(t('setNewPassword.successMessage'));

      navigateToSignIn();
    } catch (e) {
      ToastService.showError(parseApiError(e).message);
    }
  }

  function navigateToSignIn() {
    nav.replace(screens.SignIn);
  }

  const initialValues: SetNewPasswordSchemaDataType = {
    password: '',
    passwordAgain: '',
  };

  return (
    <AuthLayout
      onPressHeaderButton={navigateToSignIn}
      headerButtonText={t('setNewPassword.signIn')}
    >
      <AuthFormLayout title={t('setNewPassword.title')}>
        <Form<SetNewPasswordSchemaDataType>
          initialValues={initialValues}
          onSubmit={setNewPassword}
          validationSchema={setNewPasswordSchema}
          validateOnMount
        >
          <Field
            name="password"
            secureTextEntry
            placeholder={t('setNewPassword.password')}
          />
          <Separator />
          <Field
            name="passwordAgain"
            secureTextEntry
            placeholder={t('setNewPassword.confirmPassword')}
          />
          <Separator />
          <FormButton>{t('setNewPassword.create')}</FormButton>
          {IS_MOBILE && (
            <Fragment>
              <Separator size="$5" />
              <Button
                iconLeft="small-arrow-left-light"
                onPress={navigateToSignIn}
                buttonStyle={ButtonStyleEnum.Outline}
              >
                {t('setNewPassword.goToSignIn')}
              </Button>
            </Fragment>
          )}
        </Form>
      </AuthFormLayout>
    </AuthLayout>
  );
});
